import {
  AdminEditUserPermission,
  AdminHead,
  AdminSettingUserCompany,
} from "../components";
import { FormEvent, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getAdminUser,
  GetAdminUserState,
  resetAdminUser,
  selectGetAdminUser,
} from "../slices/get-admin-user.slice";
import {
  getAdminGroups,
  selectGetAdminGroups,
} from "../slices/get-admin-groups.slice";
import {
  editAdminUser,
  EditAdminUserState,
  resetEditAdminUser,
  selectEditAdminUser,
} from "../slices/edit-admin-user.slice";
import {
  MaterialInput,
  MaterialInputAutoComplete,
  MaterialInputPassword,
  MaterialPhoneInput,
} from "features/shared/presentation/components";
import { CircularProgress, Divider, Switch } from "@mui/material";
import { GroupModel } from "features/admin/core/domain/group.model";
import {
  adminSettingsEditUserMaterialSupplierCustomer,
  selectAdminSettingsEditUserMaterialSupplierCustomer,
} from "../slices/admin-settings-edit-user-meterial-supplier-customer.slice";
import { AdminSettingsEditUserMaterialSupplierCustomerModel } from "features/admin/core/domain/admin-settings-edit-user-material-supplier-customer.model";
import { PermStateModel } from "../components/admin-edit-user-permission-selection";
import { getAdminSession } from "../slices/get-admin-session.slice";

interface formStateTypes {
  firstName: string;
  lastName: string;
  company: number | null;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  testMode: boolean;
  groups: Array<number> | null;
  stock_order_group: Array<number> | null;
  stock_order_supplier: Array<Number> | null;
  stock_order_group_seller: Array<number> | null;
  stock_seller_company: number | null;
  sales_group: Array<number> | null;
  hubLink_group: Array<number> | null;
  hubLink_store: Array<number> | null;
}

export function AdminSettingEditUser() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const getAdminUserState = useAppSelector(selectGetAdminUser);
  const getAdminGroupsState = useAppSelector(selectGetAdminGroups);
  const editAdminUserState = useAppSelector(selectEditAdminUser);
  const adminSettingsEditUserMaterialSupplierCustomerState = useAppSelector(
    selectAdminSettingsEditUserMaterialSupplierCustomer
  );

  const [formState, setFormState] = useState<formStateTypes>({
    firstName: "",
    lastName: "",
    company: null,
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    groups: null,
    testMode: false,
    stock_order_group: null,
    stock_order_supplier: [],
    stock_order_group_seller: null,
    stock_seller_company: null,
    sales_group: null,
    hubLink_group: null,
    hubLink_store: null,
  });

  useEffect(() => {
    if (id) {
      dispatch(resetAdminUser());
      dispatch(getAdminUser(id));
      dispatch(getAdminGroups());
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (adminSettingsEditUserMaterialSupplierCustomerState.data === undefined) {
      dispatch(adminSettingsEditUserMaterialSupplierCustomer());
    }
  }, [adminSettingsEditUserMaterialSupplierCustomerState.data]);

  useEffect(() => {
    if (
      getAdminUserState.status === GetAdminUserState.success &&
      getAdminUserState.data &&
      adminSettingsEditUserMaterialSupplierCustomerState.data
    ) {
      const matchingCustomerOptions =
        adminSettingsEditUserMaterialSupplierCustomerState.data?.supplier
          .filter((cust) =>
            getAdminUserState.data?.stockOrderSuppliers.find(
              (sup) => cust.id === sup.customer_company_id
            )
          )
          .map((selectedSup) => selectedSup.id) ?? [];

      const matchingSupplierCompany =
        getAdminUserState.data?.stockSellerCompany?.supplier_company_id ?? null;

      setFormState({
        ...formState,
        firstName: getAdminUserState.data.first_name,
        lastName: getAdminUserState.data.last_name,
        email: getAdminUserState.data.email,
        company: getAdminUserState.data.companyId,
        phoneNumber: getAdminUserState.data.phone,
        password: "",
        confirmPassword: "",
        testMode: getAdminUserState.data.testMode,
        stock_order_supplier: matchingCustomerOptions,
        stock_seller_company: matchingSupplierCompany,
      });
    }
  }, [getAdminUserState]);

  useEffect(() => {
    if (editAdminUserState.status === EditAdminUserState.success) {
      dispatch(resetEditAdminUser());
      navigate("/admin/setting/user");
    }
  }, [editAdminUserState, navigate, dispatch]);

  const handleInputChange = (evt: any) => {
    const value = evt.target.value;
    setFormState({
      ...formState,
      [evt.target.name]: value,
    });
  };

  const handleSelectionChange = (
    selectedOptions: Array<
      AdminSettingsEditUserMaterialSupplierCustomerModel["supplier"]
    >,
    property: string
  ) => {
    let selectedIds: number[] = [];

    selectedOptions.map((opt: any) => selectedIds.push(opt.id));

    setFormState((prev) => ({
      ...prev,
      [property]: selectedIds,
    }));
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    if (id) {
      dispatch(
        editAdminUser({
          userId: id,
          body: formState,
        })
      );
    }
    e.preventDefault();
  };

  const handlePermissionSelection =
    (property: string) => (perms: PermStateModel | null) => {
      let group: Array<number> = [];
      if (perms && perms[property]) {
        perms[property]?.map((prm) => group.push(prm));
      }
      setFormState((prev) => ({
        ...prev,
        [property]: group,
      }));
    };

  // useEffect(() => {
  //   console.table(formState.company);
  // }, [formState]);

  return (
    <>
      <AdminHead
        AdminBreadCrumbsProps={{
          home: {
            title: "Home",
            url: "/admin",
          },
          className: "lg:h-[200px]",
          pageTitles: [
            { name: "User", url: "/admin/setting/user" },
            { name: "Edit User", url: location.pathname },
          ],
        }}
      />

      <div className="px-4">
        <div className="flex justify-between">
          <span className="text-secondary text-3xl font-['Bebas_Neue'] flex-1">
            Edit User
          </span>

          <span className="self-center">Test mode</span>
          <Switch
            checked={formState.testMode}
            onChange={(event, check) =>
              setFormState({ ...formState, testMode: check })
            }
          />
        </div>

        <div className="pb-10 space-y-6">
          <span>Please enter the user's information below.</span>

          {getAdminUserState?.data && getAdminGroupsState.data ? (
            <form onSubmit={onSubmit} className="flex flex-col space-y-4">
              <MaterialInput
                colorTheme="black"
                required
                label="First Name"
                name="firstName"
                value={formState.firstName}
                onChange={handleInputChange}
              />
              <MaterialInput
                colorTheme="black"
                required
                label="Last Name"
                name="lastName"
                value={formState.lastName}
                onChange={handleInputChange}
              />

              <AdminSettingUserCompany
                company={formState.company}
                setCompany={(compId) =>
                  setFormState((prev) => ({
                    ...prev,
                    company: compId,
                  }))
                }
              />

              <MaterialPhoneInput
                colorTheme="black"
                onChange={handleInputChange}
                value={formState.phoneNumber}
                name="phoneNumber"
              />

              <MaterialInputPassword
                colorTheme="black"
                onChange={handleInputChange}
                value={formState.password}
                name="password"
                label="Password"
              />
              <MaterialInputPassword
                colorTheme="black"
                onChange={handleInputChange}
                value={formState.confirmPassword}
                name="confirmPassword"
                label="Confirm Password"
              />

              <AdminEditUserPermission
                title={"User Groups"}
                groupData={getAdminGroupsState.data.shop}
                adminGroupState={getAdminUserState.data.groups}
                property={"groups"}
                setGroupPermissions={handlePermissionSelection("groups")}
              />

              <div className="flex w-full justify-evenly space-x-5">
                {/* Buyer */}
                {formState.stock_order_supplier !== undefined ? (
                  <div className="space-y-2 w-full">
                    <h3 className="text-xl">Stock Ordering</h3>
                    <MaterialInputAutoComplete
                      colorTheme={"black"}
                      size="small"
                      options={
                        adminSettingsEditUserMaterialSupplierCustomerState.data
                          ?.supplier ?? []
                      }
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option === value}
                      onChange={(evt, selectedOption) => {
                        handleSelectionChange(
                          selectedOption,
                          "stock_order_supplier"
                        );
                      }}
                      value={
                        adminSettingsEditUserMaterialSupplierCustomerState.data?.supplier?.filter(
                          (cust) =>
                            formState.stock_order_supplier?.includes(cust.id) ??
                            false
                        ) || []
                      }
                      multiple
                      placeholder="Supplier selection"
                      filterSelectedOptions
                    />
                    <p className="text-sm text-gray-400">Select supplier</p>

                    <AdminEditUserPermission
                      title={"Buyer Permission Selection"}
                      groupData={getAdminGroupsState.data?.stock_order}
                      adminGroupState={getAdminUserState.data.stockOrderGroup}
                      property={"stock_order_group"}
                      setGroupPermissions={handlePermissionSelection(
                        "stock_order_group"
                      )}
                    />
                  </div>
                ) : (
                  <CircularProgress />
                )}

                <Divider orientation="vertical" flexItem />

                {/* Seller */}
                {formState.stock_seller_company !== undefined ? (
                  <div className="space-y-2 w-full">
                    <h3 className="text-xl">Stock Selling</h3>

                    <MaterialInputAutoComplete
                      colorTheme={"black"}
                      size="small"
                      options={
                        adminSettingsEditUserMaterialSupplierCustomerState.data
                          ?.supplier ?? []
                      }
                      getOptionLabel={(option) => option.name}
                      value={adminSettingsEditUserMaterialSupplierCustomerState.data?.supplier.find(
                        (comp) => formState.stock_seller_company === comp.id
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      onChange={(evt, selectedOption) => {
                        if (selectedOption !== null) {
                          setFormState((prev) => ({
                            ...prev,
                            stock_seller_company: selectedOption.id,
                          }));
                        }
                      }}
                      placeholder="Company selection"
                    />
                    <p className="text-sm text-gray-400">Select company</p>

                    <AdminEditUserPermission
                      title={"Seller Permission Selection"}
                      groupData={getAdminGroupsState.data?.stock_order}
                      adminGroupState={
                        getAdminUserState.data.stockOrderGroupSeller
                      }
                      property={"stock_order_group_seller"}
                      setGroupPermissions={handlePermissionSelection(
                        "stock_order_group_seller"
                      )}
                    />
                  </div>
                ) : (
                  <CircularProgress />
                )}
              </div>

              <button
                type="submit"
                className="px-4 py-2 text-white rounded-lg bg-button w-fit"
              >
                Edit User
              </button>
            </form>
          ) : null}
        </div>
      </div>
    </>
  );
}
